import { useCallback, useEffect, useRef } from 'react';
import type { AuthModel, FlowSessionSource } from '../model/auth.types';
import type { RegisterStep } from './register.types';

type RegisterSessionLifecycleInput = {
  model: AuthModel;
  sessionSource: FlowSessionSource;
  step: RegisterStep;
  email: string;
};

type RegisterSessionLifecycle = {
  stopSessionPersistence: () => void;
};

export const useRegisterSessionLifecycle = ({
  model,
  sessionSource,
  step,
  email,
}: RegisterSessionLifecycleInput): RegisterSessionLifecycle => {
  const hasInitializedSignupAttributionRef = useRef(false);
  const shouldPersistSessionRef = useRef(true);

  useEffect(() => {
    if (sessionSource !== 'signup') {
      return;
    }

    if (hasInitializedSignupAttributionRef.current) {
      return;
    }

    hasInitializedSignupAttributionRef.current = true;
    model.dispatch('InitRegistrationAttribution');
  }, [model, sessionSource]);

  useEffect(() => {
    if (!shouldPersistSessionRef.current) {
      return;
    }

    model.dispatch('PersistFlowSession', {
      step,
      source: sessionSource,
      email,
    });
  }, [email, model, sessionSource, step]);

  const stopSessionPersistence = useCallback((): void => {
    shouldPersistSessionRef.current = false;
  }, []);

  return {
    stopSessionPersistence,
  };
};
